import { useMemo } from "react";
import { TEST_IDS } from "@/constants/testIds";

const nf = new Intl.NumberFormat(undefined, { maximumSignificantDigits: 4 });

/**
 * Consensus visualisation: number line of submitted values + histogram
 * distribution + high-level statistics grid.
 */
export default function ConsensusPanel({ consensus, unit }) {
    const values = useMemo(() => consensus?.values || [], [consensus]);
    const mn = consensus?.min ?? 0;
    const mx = consensus?.max ?? 1;
    const span = (mx - mn) || 1;
    const dots = useMemo(() => {
        return (consensus?.values || []).map((v, i) => ({
            v,
            leftPct: ((v - mn) / span) * 100,
            key: i,
        }));
    }, [consensus?.values, mn, span]);

    if (!consensus || !consensus.count) return null;

    const { min, max, average, median, std_dev, consensus_score,
            distribution, most_accepted, count } = consensus;

    const histMax = Math.max(1, ...(distribution || []).map((d) => d.count));

    return (
        <div
            data-testid={TEST_IDS.consensusPanel}
            className="border border-neutral-200 bg-white rounded-xl p-5 shadow-sm"
        >
            <div className="label-caps mb-4">Value distribution & community review</div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-5 border-b border-neutral-100">
                <Stat label="Community review" value={consensus_score == null ? "Not yet reviewed" : `${consensus_score}%`}
                      accent={consensus_score >= 80 ? "#16A34A" :
                              consensus_score >= 60 ? "#EAB308" : "#F97316"} />
                <Stat label="Submissions" value={count} />
                <Stat label="Median" value={nf.format(median)} suffix={unit} featured />
                <Stat label="Range" value={`${nf.format(min)}–${nf.format(max)}`} suffix={unit} featured />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            {/* Number line with dots */}
            <div className="border border-neutral-200 rounded-lg p-4">
                <div className="label-caps mb-3">Values submitted</div>
                <div className="relative h-14 mt-2">
                    <div className="absolute left-0 right-0 top-1/2 h-px bg-neutral-300" />
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-neutral-800" />
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-2 h-2 bg-neutral-800" />
                    {dots.map((d) => (
                        <div
                            key={d.key}
                            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-[#6B46C1] border border-white shadow-sm"
                            style={{ left: `${d.leftPct}%` }}
                            title={`${nf.format(d.v)} ${unit}`}
                        />
                    ))}
                    {/* median marker */}
                    <div
                        className="absolute top-1 -translate-x-1/2 h-14 w-px bg-[#FFD60A]"
                        style={{ left: `${((median - min) / span) * 100}%` }}
                    >
                        <div className="absolute -top-4 -translate-x-1/2 text-[9px] label-caps text-neutral-500">
                            median
                        </div>
                    </div>
                    {/* avg marker */}
                    <div
                        className="absolute -bottom-2 -translate-x-1/2 h-14 w-px bg-[#6B46C1] opacity-40"
                        style={{ left: `${((average - min) / span) * 100}%` }}
                    >
                        <div className="absolute -bottom-4 -translate-x-1/2 text-[9px] label-caps text-neutral-500">
                            avg
                        </div>
                    </div>
                </div>
                <div className="mt-4 flex justify-between text-[10px] font-mono text-neutral-500">
                    <span>{nf.format(min)}</span>
                    <span>{nf.format(max)}</span>
                </div>
            </div>

            {/* Histogram */}
            <div
                data-testid={TEST_IDS.distributionChart}
                className="border border-neutral-200 rounded-lg p-4"
            >
                <div className="label-caps mb-3">Distribution</div>
                <div className="flex items-end gap-px h-20 pt-2 overflow-hidden rounded-t-lg bg-gradient-to-t from-violet-50 to-transparent">
                    {(distribution || []).map((d, i) => {
                        const h = (d.count / histMax) * 100;
                        return (
                            <div
                                key={i}
                                className="flex-1 bg-violet-400/70 border-t border-x border-violet-500 hover:bg-violet-500 transition-colors relative group"
                                style={{ height: `${Math.max(h, 3)}%` }}
                                title={`${d.count} submissions in ${nf.format(d.bin_start)}–${nf.format(d.bin_end)}`}
                            >
                                <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-mono opacity-0 group-hover:opacity-100">
                                    {d.count}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="mt-1 flex justify-between text-[10px] font-mono text-neutral-500">
                    <span>{nf.format(min)}</span>
                    <span>{nf.format(max)}</span>
                </div>
            </div></div>

            {/* Most accepted */}
            {most_accepted && (
                <div className="mt-3 border border-[#8B5CF6] bg-gradient-to-r from-[#F5F3FF] to-white p-4 rounded-lg">
                    <div className="label-caps mb-1 text-[#4C1D95]">Most-accepted submission</div>
                    <div className="flex items-baseline justify-between gap-4">
                        <div className="font-serif text-lg">
                            {most_accepted.organization_name}
                        </div>
                        <div className="font-mono text-xl text-[#4C1D95]">
                            {nf.format(most_accepted.value)}
                            <span className="text-neutral-500 text-sm ml-1">{unit}</span>
                        </div>
                    </div>
                    <div className="mt-1 text-xs text-neutral-600">
                        ↑ {most_accepted.agree_count} agree · ↓ {most_accepted.disagree_count} disagree
                    </div>
                </div>
            )}

            <div className="mt-3 text-[10px] text-neutral-500 leading-relaxed flex flex-wrap justify-between gap-2">
                σ = {nf.format(std_dev)} · Average {nf.format(average)} {unit}.
                Value clustering does not establish independent verification. Community review requires source evidence and at least 3 votes.
            </div>
        </div>
    );
}

const Stat = ({ label, value, suffix, accent, featured = false }) => (
    <div className={featured
        ? "rounded-lg border border-violet-200 bg-violet-50/70 px-3 py-2 -my-2"
        : "border-r border-dashed border-neutral-200 pr-3"
    }>
        <div className={`label-caps ${featured ? "text-violet-700" : ""}`}>{label}</div>
        <div
            className={`font-sans mt-1 font-semibold tracking-tight ${featured ? "text-2xl text-violet-950" : "text-xl"}`}
            style={accent ? { color: accent } : {}}
        >
            {value}
        </div>
        {suffix && <div className="mt-0.5 text-[10px] font-mono text-violet-500">{suffix}</div>}
    </div>
);
